import queryString from "query-string";
import { lazy, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useNavigate } from "react-router-dom";
// import { Crisp } from "crisp-sdk-web";

import { lazyRetry } from "./RouterUtils";
import { EmbedContextProvider } from "./embed/embedContext";
import { SEMRUSH_HOST } from "./utils/constants";

import axios from "axios";

import { QueryClientProvider } from "@tanstack/react-query";
import { LazyLoader } from "./LazyLoader";
import { PermissionProvider } from "./context/permissions/PermissionContext";
// import Embed from "./embed/route";

import { queryClient } from "./indexUtils";
import { API_ADDRESS, setupInterceptors } from "./services/api";
import { chatConfig } from "./utils/chat-support-utils";
import { getListenerParent } from "./utils/common";
import { localStorageSafe } from "./utils/localStorageUtils";
import Alertcomp from "./components/common/AlertComp";
import NonPublicRouter from "./NonPublicRouter";

// import Quill from "quill";

// const Inline = Quill.import("blots/inline");

// class FontWeightBlot extends Inline {
//   static create(value) {
//     const node = super.create();
//     node.style.fontWeight = value;
//     return node;
//   }

//   static formats(node) {
//     return node.style.fontWeight;
//   }
// }

// FontWeightBlot.blotName = "font-weight";
// FontWeightBlot.tagName = "span";

// Quill.register(FontWeightBlot);
const ShareableLink = lazy(() =>
  lazyRetry(() => import("./components/ShareableLink/ShareableLink"))
);

const params = queryString.parse(window.location.search);
const embedAppId = params.app_id || localStorageSafe("getItem", "appId");

function MainRouter() {
  const { isWebView, createPostOpen } = useSelector((state) => state.uiStates);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useMemo(() => {
    // setup interceptors on load
    setupInterceptors();
    // set global axios url
    axios.defaults.baseURL = API_ADDRESS;
  }, []);

  const { editOpen, previewOpen } = useSelector((state) => state.getPosts);

  const editOpenRef = useRef(editOpen);
  const previewOpenRef = useRef(previewOpen);
  const createPostRef = useRef(createPostOpen);

  useEffect(() => {
    editOpenRef.current = editOpen;
    previewOpenRef.current = previewOpen;
    createPostRef.current = createPostOpen;
  }, [editOpen, previewOpen, createPostOpen]);

  // useEffect(() => {
  //   we_track("sidebar_navigate", { path: location.pathname });
  // }, [location]);

  const handleBackGesture = ({ data }) => {
    try {
      if (data && String(data).startsWith("event")) {
        const [_, evName] = data.split("||");
        if (evName === "backPressed") {
          // LISTENER IS NOT REACTIVE TOWARDS STATE. WILL NOT GET UPDATED STATE VARIABLE VALUE
          if (previewOpenRef.current) {
            dispatch({ type: "getPosts/setPreviewOpen", payload: false });
          } else if (editOpenRef.current) {
            dispatch({ type: "getPosts/setEditOpen", payload: false });
          } else if (createPostRef.current) {
            dispatch({
              type: "uiStates/setCreatePostOpen",
              payload: false
            });
          } else {
            navigate(-1);
          }
        }
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    isWebView && localStorageSafe("setItem", "isWebview", true);
    isWebView && getListenerParent().addEventListener("message", handleBackGesture);

    // init semrush SDK if host is semrush this is required since if anywhere window.location.reload is called it wont load this
    // for e.g. on
    if (currentURL.host === SEMRUSH_HOST) {
      import("@semcore/app-center-js-sdk")
        .then(({ initAppCenterSDK }) => {
          initAppCenterSDK();
          window.SM.init();
        })
        .catch((error) => {
          console.error("Failed to load Semrush SDK:", error);
        });
    }
    return () => {
      isWebView && getListenerParent().removeEventListener("message", handleBackGesture);
    };
  }, []);

  useEffect(() => {
    chatConfig();
  }, []);

  const currentURL = new URL(window.location.href);

  return (
    <QueryClientProvider client={queryClient}>
      <Alertcomp />
      <Routes>
        {/* Public share routes — minimal providers */}
        <Route
          path='/shareable-link/:share_id'
          element={
            <LazyLoader>
              <ShareableLink />
            </LazyLoader>
          }
        />
        <Route
          path='/*'
          element={
            <PermissionProvider>
              <EmbedContextProvider embedAppId={embedAppId}>
                <NonPublicRouter />
              </EmbedContextProvider>
            </PermissionProvider>
          }
        />
      </Routes>
    </QueryClientProvider>
  );
}

export default MainRouter;
